import { type ImportSheetDescriptor } from '@utazzo/shared';
import { UzAlert, UzBadge, UzButton, UzCard, UzSelect, UzSpinner } from '@utazzo/ui';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  Cloud,
  Download,
  FileSpreadsheet,
  Hotel,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageShell } from '@/components/page-shell';
import { JobSummary } from '@/features/lead-imports/components/JobSummary';
import { RowCard } from '@/features/lead-imports/components/RowCard';
import { SampleTemplateButton } from '@/features/lead-imports/components/SampleTemplateButton';
import {
  useCommitImport,
  useImportJob,
  useImportModules,
  useImportRows,
  useImportSourceFile,
  useUploadImport,
} from '@/features/lead-imports/use-imports';
import { useListReturn } from '@/hooks/use-list-return';

/**
 * Hotels Imports wizard (ADR-0036 v2). Same lifecycle as Leads but with
 * tabbed multi-sheet preview:
 *
 *   1. /hotels/imports/new            → Upload step.
 *   2. /hotels/imports/jobs/:jobId    → Sheet tabs → fix → commit.
 */
export const ImportHotelsPage = (): React.JSX.Element => {
  const { jobId } = useParams<{ jobId?: string }>();
  if (!jobId) return <HotelsUploadStep />;
  return <HotelsReviewStep jobId={jobId} />;
};

const MODULE_KEY = 'hotel';

// ---------- Upload step ----------

const HotelsUploadStep = (): React.JSX.Element => {
  const navigate = useNavigate();
  const backToList = useListReturn('/hotels');
  const modulesQuery = useImportModules();
  const upload = useUploadImport();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const activeModule = (modulesQuery.data ?? []).find((m) => m.moduleKey === MODULE_KEY);
  const sheets = activeModule?.sheets ?? [];

  const handleUpload = async (): Promise<void> => {
    if (!file) return;
    const { jobId } = await upload.mutateAsync({ moduleKey: MODULE_KEY, file });
    navigate(`/hotels/imports/jobs/${jobId}`);
  };

  return (
    <PageShell
      title="Import hotels"
      description="Upload a multi-sheet XLSX containing hotels + rooms + inventory + meal options + supplier mappings."
      actions={
        <div className="flex gap-2">
          <UzButton variant="ghost" size="sm" onClick={backToList}>
            <Hotel className="mr-1.5 h-4 w-4" /> Hotels list
          </UzButton>
          <UzButton variant="ghost" size="sm" onClick={() => navigate('/hotels/imports')}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Audit log
          </UzButton>
        </div>
      }
    >
      <div className="mx-auto max-w-3xl space-y-4">
        <UzCard
          title="XLSX file"
          description={`Max 10MB. The workbook must contain the sheets listed below (extra sheets are ignored). Headers in each sheet match the column labels - case + spaces are tolerated.`}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <div className="flex items-center gap-3">
            <UzButton variant="outline" onClick={() => fileRef.current?.click()}>
              <FileSpreadsheet className="mr-1.5 h-4 w-4" />
              Pick XLSX
            </UzButton>
            <div className="text-sm text-zinc-700">
              {file ? (
                <>
                  <strong>{file.name}</strong>
                  <span className="ml-2 text-zinc-500">{(file.size / 1024).toFixed(1)} KB</span>
                </>
              ) : (
                'No file selected.'
              )}
            </div>
            {file && (
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-xs text-zinc-500 hover:text-zinc-700"
              >
                Clear
              </button>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-2">
            <SampleTemplateButton moduleKey={MODULE_KEY} />
            <UzButton onClick={handleUpload} loading={upload.isPending} disabled={!file}>
              <Cloud className="mr-1.5 h-4 w-4" />
              Upload + validate
            </UzButton>
          </div>
        </UzCard>

        <UzAlert tone="info">
          <strong>HotelKey, RoomKey, InventoryKey</strong> are operator-supplied stable identifiers
          (letters, digits, dash, underscore) joining sheets within ONE upload. They are NOT
          persisted - the importer resolves them to real UUIDs at commit time and throws the keys
          away.
        </UzAlert>

        {sheets.length > 0 && <SheetsOverview sheets={sheets} />}
      </div>
    </PageShell>
  );
};

const SheetsOverview = ({
  sheets,
}: {
  sheets: ReadonlyArray<ImportSheetDescriptor>;
}): React.JSX.Element => (
  <UzCard title="Expected sheets" description={`${sheets.length} sheets in the workbook.`}>
    <div className="space-y-3">
      {sheets.map((sheet) => (
        <div key={sheet.key} className="rounded-md border border-zinc-200 p-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-zinc-900">{sheet.label}</span>
            <span className="font-mono text-xs text-zinc-500">[{sheet.key}]</span>
            {sheet.required && <UzBadge tone="destructive">required</UzBadge>}
          </div>
          {sheet.description && <p className="mt-1 text-xs text-zinc-500">{sheet.description}</p>}
          <div className="mt-2 flex flex-wrap gap-1.5 text-xs text-zinc-600">
            {sheet.columns.map((c) => (
              <span
                key={c.field}
                className={`rounded border px-1.5 py-0.5 ${
                  c.required
                    ? 'border-amber-200 bg-amber-50 text-amber-800'
                    : 'border-zinc-200 bg-white'
                }`}
                title={c.hint ?? c.field}
              >
                {c.label}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </UzCard>
);

// ---------- Review step ----------

const STATUS_FILTERS = [
  { value: 'ALL', label: 'All rows' },
  { value: 'INVALID', label: 'Invalid' },
  { value: 'VALID', label: 'Valid' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'COMMITTED', label: 'Committed' },
  { value: 'COMMIT_FAILED', label: 'Failed' },
] as const;

const HotelsReviewStep = ({ jobId }: { jobId: string }): React.JSX.Element => {
  const navigate = useNavigate();
  const backToList = useListReturn('/hotels');
  const job = useImportJob(jobId);
  const modulesQuery = useImportModules();
  const moduleDescriptor = (modulesQuery.data ?? []).find(
    (m) => m.moduleKey === job.data?.moduleKey,
  );
  // Memoise so the useEffect dep-array below doesn't churn on every render
  // (the `?? []` literal would otherwise be a new array each time).
  const sheets = useMemo(() => moduleDescriptor?.sheets ?? [], [moduleDescriptor]);
  const [activeSheet, setActiveSheet] = useState<string>(sheets[0]?.key ?? '');
  useEffect(() => {
    if (!activeSheet && sheets.length > 0) setActiveSheet(sheets[0]!.key);
  }, [activeSheet, sheets]);

  const [statusFilter, setStatusFilter] =
    useState<(typeof STATUS_FILTERS)[number]['value']>('INVALID');
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [activeSheet, statusFilter]);

  const rows = useImportRows(jobId, {
    page,
    pageSize: 25,
    sortBy: 'rowIndex',
    sortDir: 'asc',
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    sheetKey: activeSheet,
  });
  const commit = useCommitImport(jobId, { invalidateOnSuccess: ['hotels', 'hotel-rooms'] });

  // Lazy-load the source-file presigned URL only when the operator
  // clicks "Download source XLSX". We park the click intent and read
  // the URL from the query once it lands.
  const [sourceRequested, setSourceRequested] = useState(false);
  const sourceQuery = useImportSourceFile(jobId, sourceRequested);
  useEffect(() => {
    if (!sourceRequested || !sourceQuery.data) return;
    const url = sourceQuery.data.url;
    if (url) {
      // Trigger the download. The presigned URL responds with the right
      // Content-Disposition so the browser saves a filename like the
      // original upload.
      window.open(url, '_self', 'noopener,noreferrer');
    }
    setSourceRequested(false);
  }, [sourceRequested, sourceQuery.data]);

  const activeSheetDescriptor = sheets.find((s) => s.key === activeSheet);
  const columns = activeSheetDescriptor?.columns ?? [];

  const canCommit =
    !!job.data &&
    (job.data.status === 'READY' || job.data.status === 'PARTIAL') &&
    job.data.invalidRows === 0 &&
    job.data.validRows > 0;

  if (job.isLoading || !job.data) {
    return (
      <PageShell title="Loading import job…" description="">
        <UzCard>
          <div className="flex items-center gap-2 p-6 text-sm text-zinc-500">
            <UzSpinner size="sm" /> Fetching status…
          </div>
        </UzCard>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={`Import - ${job.data.fileName}`}
      description={`${job.data.totalRows} rows across ${sheets.length} sheet(s) · ${job.data.moduleKey} module`}
      actions={
        <div className="flex gap-2">
          <UzButton variant="ghost" size="sm" onClick={backToList}>
            <Hotel className="mr-1.5 h-4 w-4" /> Hotels list
          </UzButton>
          <UzButton variant="ghost" size="sm" onClick={() => navigate('/hotels/imports')}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Audit log
          </UzButton>
          <UzButton
            variant="outline"
            size="sm"
            onClick={() => setSourceRequested(true)}
            loading={sourceRequested && sourceQuery.isFetching}
          >
            <Download className="mr-1.5 h-4 w-4" /> Source XLSX
          </UzButton>
          <UzButton
            onClick={() => commit.mutateAsync()}
            loading={commit.isPending}
            disabled={!canCommit}
          >
            <Check className="mr-1.5 h-4 w-4" />
            Commit {job.data.validRows} row(s)
          </UzButton>
        </div>
      }
    >
      <div className="space-y-3">
        {sourceQuery.data && sourceQuery.data.purgedAt && (
          <UzAlert tone="info">
            <AlertCircle className="mr-1 inline h-3.5 w-3.5" />
            The source XLSX was purged on{' '}
            {new Date(sourceQuery.data.purgedAt).toLocaleString('en-IN')} (past the retention
            window). Audit rows are still available below.
          </UzAlert>
        )}

        <JobSummary job={job.data} />

        {/* Sheet tabs */}
        {sheets.length > 0 && (
          <UzCard className="!p-0">
            <div className="flex flex-wrap gap-1 border-b border-zinc-200 px-3 pt-3">
              {sheets.map((sheet) => (
                <button
                  type="button"
                  key={sheet.key}
                  onClick={() => setActiveSheet(sheet.key)}
                  className={`rounded-t-md border border-b-0 px-3 py-1.5 text-xs font-medium transition-colors ${
                    activeSheet === sheet.key
                      ? 'border-zinc-200 bg-white text-zinc-900'
                      : 'border-transparent text-zinc-500 hover:text-zinc-700'
                  }`}
                >
                  {sheet.label}
                </button>
              ))}
            </div>

            <div className="space-y-3 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-zinc-500">
                  Showing the <strong>{activeSheetDescriptor?.label}</strong> sheet · row index
                  refers to the row number inside that sheet.
                </p>
                <div className="w-40">
                  <UzSelect
                    name="status-filter"
                    options={
                      STATUS_FILTERS as unknown as ReadonlyArray<{ value: string; label: string }>
                    }
                    value={statusFilter}
                    onValueChange={(v) =>
                      v && setStatusFilter(v as (typeof STATUS_FILTERS)[number]['value'])
                    }
                  />
                </div>
              </div>

              {rows.isLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-zinc-500">
                  <UzSpinner size="sm" /> Loading rows…
                </div>
              ) : (rows.data?.data ?? []).length === 0 ? (
                <div className="py-6 text-center text-sm text-zinc-500">
                  No rows on this sheet match the current filter.
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {(rows.data?.data ?? []).map((row) => (
                      <RowCard
                        key={row.id}
                        row={row}
                        columns={columns}
                        jobId={jobId}
                        sheetKey={activeSheet}
                        summaryFields={summaryFieldsForSheet(activeSheet)}
                      />
                    ))}
                  </div>
                  {(rows.data?.pagination.totalPages ?? 1) > 1 && (
                    <div className="mt-3 flex items-center justify-between border-t border-zinc-100 pt-3 text-xs text-zinc-600">
                      <span>
                        Page {rows.data?.pagination.page} of {rows.data?.pagination.totalPages} ·{' '}
                        {rows.data?.pagination.totalItems} row
                        {rows.data?.pagination.totalItems === 1 ? '' : 's'}
                      </span>
                      <div className="flex gap-1">
                        <UzButton
                          size="sm"
                          variant="ghost"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={!rows.data?.pagination.hasPrev || rows.isFetching}
                        >
                          Prev
                        </UzButton>
                        <UzButton
                          size="sm"
                          variant="ghost"
                          onClick={() => setPage((p) => p + 1)}
                          disabled={!rows.data?.pagination.hasNext || rows.isFetching}
                        >
                          Next
                        </UzButton>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </UzCard>
        )}
      </div>
    </PageShell>
  );
};

const summaryFieldsForSheet = (sheetKey: string): ReadonlyArray<string> => {
  switch (sheetKey) {
    case 'Hotels':
      return ['hotelKey', 'name', 'city'];
    case 'Rooms':
      return ['hotelKey', 'roomKey', 'name'];
    case 'RoomMealOptions':
      return ['hotelKey', 'roomKey', 'mealPlan'];
    case 'Inventory':
      return ['hotelKey', 'roomKey', 'inventoryKey'];
    case 'InventoryMealOptions':
      return ['hotelKey', 'inventoryKey', 'mealPlan'];
    case 'SupplierMappings':
      return ['hotelKey', 'supplier', 'externalHotelId'];
    default:
      return [];
  }
};
